/**
 * Exposure (Phase 57.5 / 57.6): how visible a spot is to the guards an
 * infiltrator knows about — the guards' own perception geometry (range by
 * their planet's alert level, a 120° cone when they have a facing, terrain
 * line of sight) applied to what the bot knows: enemies it can see now (with
 * facing) and ghosts (last seen position, treated as looking everywhere).
 * Pure; shared by the sneaking walk and the sneaking jump.
 */

import type { PlayerState, GameState, BotFogContext, Vec3, PlanetState } from '../../types';
import { vecNormalize, vecSub, vecDot, vecLength } from '../../math';
import { hasLineOfSight, losEyePoint } from '../../core/los';
import { alertLevelAt, perceptionRange, tangentToward } from '../../core/perception';
import { PERCEPTION_CONE_HALF_ANGLE } from '../../constants';
import { dcos } from '../../core/dmath';
import { weatherEffectHere, weatherEffect } from '../../core/weather';

export interface KnownGuard {
  eye: Vec3;
  /** Unit tangent the guard faces, or undefined (looks everywhere). */
  facing?: Vec3;
  normal: Vec3;
  range: number;
}

/** The guards an infiltrator knows about on `planetIndex`: visible enemies there with facing, ghosts there without. */
export function knownGuardsOn(player: PlayerState, state: GameState, planetIndex: number, fog?: BotFogContext): KnownGuard[] {
  const planet = state.planets[planetIndex];
  if (!planet) return [];
  const level = alertLevelAt(state, planetIndex);
  const myStealth = player.modifiers?.stealthMult ?? 1;
  const out: KnownGuard[] = [];
  const seen = new Set<number>();
  const weatherMult = weatherEffectHere(state, planetIndex, player.surfaceDir).perception;   // 62.18: the bot sees the weather the guards see in; 62.54: read where the bot is (each guard's own place is step 6's line)
  const enemies = state.players.filter(p => p.alive && p.teamIndex !== player.teamIndex && p.planetIndex === planetIndex && !p.airborne);
  for (const e of enemies) {
    if (fog && !fog.visibleEnemyIds.includes(e.id)) continue;
    seen.add(e.id);
    const eye = losEyePoint(e, state.planets);
    // 37.20 #8: a guard's sensor kit cannot be seen — assume a plain guard; facing stays (it is inferable from how
    // the unit was last seen moving or shooting)
    const range = perceptionRange(level) * myStealth * weatherMult;
    out.push({ eye, facing: e.facing, normal: vecNormalize(vecSub(eye, planet.position)), range });
  }
  if (fog) {
    for (const g of fog.ghosts) {
      if (seen.has(g.playerId)) continue;
      const e = state.players.find(p => p.id === g.playerId);
      if (!e || !e.alive) continue;
      // A ghost is where the enemy was last seen; count it only if that spot is on this planet
      if (vecLength(vecSub(g.position, planet.position)) > planet.radius * 1.5) continue;
      out.push({ eye: g.position, normal: vecNormalize(vecSub(g.position, planet.position)), range: perceptionRange(level) * myStealth * weatherMult });
    }
  }
  return out;
}

/** How exposed a spot is: 1 per guard that would perceive a unit standing there, ½ per guard in range but looking away, ¼ behind terrain. */
export function exposureAt(eye: Vec3, guards: KnownGuard[], planets: PlanetState[]): number {
  let total = 0;
  const cosHalf = dcos(PERCEPTION_CONE_HALF_ANGLE);
  for (const g of guards) {
    const dist = vecLength(vecSub(eye, g.eye));
    if (dist > g.range) continue;
    if (g.facing) {
      const toward = tangentToward(g.normal, g.eye, eye);
      if (toward && vecDot(vecNormalize(g.facing), toward) < cosHalf) { total += 0.5; continue; }
    }
    total += hasLineOfSight(g.eye, eye, planets) ? 1 : 0.25;
  }
  return total;
}
